import { useRef, useState } from 'react';
import './league-settings.styles.scss';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { playoffSpots } from '@shared/postseasonRace.js';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import DailyGames from './daily-games.component';

// A league's settings (read-only once created) and the reset/delete tools.
const LeagueSettings = ({ league }) => {

    const [ busy, setBusy ] = useState(null);
    const [ confirmMessage, setConfirmMessage ] = useState(null);
    const [ alertMessage, setAlertMessage ] = useState(null);
    const approveRef = useRef();
    const navigate = useNavigate();
    const s = league.settings;

    function confirm(message, action) {
        approveRef.current = action;
        setConfirmMessage(message);
    }

    async function run(label, command, data, after) {
        setBusy(label);
        const res = await UseCloudFunction(command, { leagueId: league.id, ...data });
        setBusy(null);
        if (res.error) {
            setAlertMessage(res.error);
            return;
        }
        if (after) after();
        else setAlertMessage(res.success);
    }

    const rows = [
        ['Type', league.type === 'pro' ? 'Pro' : 'College'],
        ['Teams', `${s.teamCount} in ${s.conferenceCount} conferences (${league.source === 'upload' ? 'uploaded' : 'generated'})`],
        ['Season', `${s.gamesPerTeam} games, ${s.conferenceGames} in conference, ${league.season.totalDays} game days`],
        ...(s.postseason.conferenceTournaments ? [['Conference tournaments', `Top ${s.postseason.conferenceTournamentSize} teams`]] : []),
        [league.type === 'pro' ? 'Playoffs' : 'National tournament', `${s.postseason.fieldSize} teams` +
            (league.type === 'pro' && playoffSpots(league).byConference ? ` (${playoffSpots(league).perConference} per conference)` : '')],
        ['Chemistry strength', s.chemistryStrength],
        ['Created', league.ts_created ? format(new Date(league.ts_created), 'PP') : '-'],
    ];

    return (
        <div className='league-settings'>
            <DailyGames league={league} />

            <div className='g-card'>
                <h3>Settings</h3>
                <dl className='settings-list'>
                    {rows.map(([label, value]) => (
                        <div key={label} className='settings-row'>
                            <dt>{label}</dt>
                            <dd>{value}</dd>
                        </div>
                    ))}
                </dl>
            </div>

            <div className='g-card danger-zone'>
                <h3>Reset or delete</h3>
                <div className='danger-row'>
                    <div>
                        <b>Reset the season</b>
                        <p className='meta'>Clears every result, stat and injury and goes back to day 0. Rosters stay the same.</p>
                    </div>
                    <div className='danger-buttons'>
                        <button className={`g-button ${busy ? 'disabled' : ''}`} onClick={() => confirm(
                            'Reset the season? Every result, stat and injury will be cleared.',
                            () => run('reset', 'resetSeason', { newSchedule: false }))}>
                            {busy === 'reset' ? <ThreeDotsLoader /> : 'Reset season'}
                        </button>
                        <button className={`g-button ${busy ? 'disabled' : ''}`} onClick={() => confirm(
                            'Reset the season with a brand-new schedule? Every result, stat and injury will be cleared.',
                            () => run('reschedule', 'resetSeason', { newSchedule: true }))}>
                            {busy === 'reschedule' ? <ThreeDotsLoader /> : 'Reset with new schedule'}
                        </button>
                    </div>
                </div>
                <div className='danger-row'>
                    <div>
                        <b>Delete this league</b>
                        <p className='meta'>Permanently removes the league, its teams, players and games. This can&apos;t be undone.</p>
                    </div>
                    <div className='danger-buttons'>
                        <button className={`g-button delete-btn ${busy ? 'disabled' : ''}`} onClick={() => confirm(
                            `Permanently delete ${league.name}? This can't be undone.`,
                            () => run('delete', 'deleteLeague', {}, () => navigate('/admin/leagues')))}>
                            {busy === 'delete' ? <ThreeDotsLoader /> : 'Delete league'}
                        </button>
                    </div>
                </div>
            </div>
            <ModalConfirm show={confirmMessage} cancel={() => setConfirmMessage(null)} message={confirmMessage} onApprove={approveRef.current} />
            <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
        </div>
    )
}

export default LeagueSettings
